import type { CSSProperties } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { AdImage, SmoothType } from '../types';
import { debounce, generateSrcSet, loadImage, mod } from '../utils';

type CarouselMobileProps = {
    currentImage: number;
    images: AdImage[];
    smooth: SmoothType;
    fallbackUrl: string | undefined;
    programmaticRatioOverride?: boolean;
    delayF: (currentImage: number) => void;
    programmaticRatio: boolean;
    showHud?: boolean;
    tab?: string;
    fullscreen?: boolean;
};
type State = {
    listElement?: HTMLUListElement;
    ulStyle: CSSProperties;
    sizes: string;
};

export const MobileFullscreenCarousel = (props: CarouselMobileProps) => {
    const { currentImage, images, fallbackUrl, delayF, smooth, programmaticRatio, tab, fullscreen } = props;
    const totalImages = images.length;
    const [zooming, setZooming] = useState(false);

    const initialStyle = {
        aspectRatio: '3/2',
    };
    const initialSizes = '(min-width: 1020px) 980px';

    const initialState = {
        sizes: initialSizes,
        ulStyle: initialStyle,
    };

    const [state, setState] = useState<State>(initialState);

    const setScrollPosition = (currentImage: number) => {
        if (state.listElement?.clientWidth) {
            const newPos = (currentImage || 0) * state.listElement.clientWidth;
            if (state.listElement.scrollLeft !== newPos) {
                state.listElement.scrollLeft = (currentImage || 0) * state.listElement.clientWidth;
            }
        }
    };

    const checkScrollPosition = () => {
        if (!state.listElement?.clientWidth) {
            return;
        }
        const scrollPosition = state.listElement.scrollLeft ?? 0;
        const currentImage = Math.min(Math.round(scrollPosition / state.listElement.clientWidth), images.length);
        loadImage(currentImage);
        loadImage(currentImage + 1);
        snapChecker(currentImage, state.listElement, setScrollPosition);
        delayF(currentImage);
    };

    useEffect(() => {
        const listener = () => {
            setScrollPosition(currentImage);
        };
        window.addEventListener('resize', listener);
        return () => {
            window.removeEventListener('resize', listener);
        };
    }, [state.listElement, currentImage]);

    useEffect(() => {
        if (state.listElement?.clientWidth) {
            function notScrolling(): boolean {
                const list = state.listElement;
                if (!list) return false;
                const clientWidth = list.clientWidth;
                if (!clientWidth) return false;
                const close = list.scrollLeft % clientWidth;
                // Width is typically 984, but sometimes becomes a decimal like 983.4583211 or 0.359382.
                return Math.ceil(close) === clientWidth || Math.floor(close) === 0;
            }

            if (smooth !== 'smooth' || notScrolling()) {
                setScrollPosition(currentImage);
            }

            let styleChange: CSSProperties | undefined;
            const [sizes, styles] = [initialSizes, initialStyle];

            if (programmaticRatio) {
                styleChange = {
                    // aspectRatio: '4 / 3' is 3/4 = 0.75
                    height: `${state.listElement.clientWidth * 0.75}px`,
                };
            }
            setState((s) => ({
                ...s,
                ulStyle: { ...styles, ...styleChange },
                sizes: sizes,
            }));
        }
    }, [currentImage, state.listElement, tab, fullscreen]);

    const callbackRef = useCallback(
        (callbackElement: HTMLUListElement) => {
            setState((s) => ({
                ...s,
                listElement: callbackElement,
            }));
        },
        ['big-image'],
    );

    useEffect(() => {
        loadImage(mod(currentImage, totalImages));
        loadImage(mod(currentImage + 1, totalImages));
        if (currentImage !== 0) {
            loadImage(mod(currentImage + 2, totalImages));
        }
        loadImage(mod(currentImage - 1, totalImages));
    }, [currentImage]);

    const scrollStyle = props.smooth === 'no-scroll' || zooming ? 'overflow-x-hidden' : 'overflow-x-auto';

    const ulStyles = state.ulStyle;

    return (
        <div className={`${fullscreen ? 'gallery-remaining-fullscreen' : 'hidden'} relative -mx-16 !s-bg-inverted gallery-transition`}>
            <a
                href={fallbackUrl}
                aria-live="polite"
                aria-label={`${images[currentImage]?.description || ''}. Bilde ${currentImage + 1} av ${totalImages}`}
                onClick={(e) => e.preventDefault()}
                style={{ textDecoration: 'none' }}>
                <ul
                    tabIndex={-1}
                    className={`carousel ${scrollStyle} flex overscroll-x-contain overflow-y-hidden h-full w-full mb-0`}
                    style={ulStyles}
                    ref={callbackRef}
                    id="main-carousel"
                    onScroll={checkScrollPosition}>
                    {images.map((image, index) => (
                        <CarouselListItem
                            image={image}
                            index={index}
                            currentImage={currentImage}
                            sizes={state.sizes}
                            images={images}
                            key={`main-image-${image.uri}`}
                            fullscreen={fullscreen}
                            setZooming={setZooming}
                        />
                    ))}
                </ul>
            </a>
        </div>
    );
};

type CarouselListItemProps = {
    image: AdImage;
    index: number;
    currentImage: number;
    sizes: string;
    images: AdImage[];
    fullscreen?: boolean;
    setZooming: (zooming: boolean) => void;
};

const CarouselListItem = ({ image, index, currentImage, sizes, images, fullscreen, setZooming }: CarouselListItemProps) => {
    const hasLoaded = useRef(false);
    const [scale, setScale] = useState(1);
    const [pointX, setPointX] = useState(0);
    const [pointY, setPointY] = useState(0);
    const [points, setPoints] = useState({ x: 0, y: 0 });
    const [start, setStart] = useState({ x: 0, y: 0 });
    const [isPanning, setIsPanning] = useState(false);
    const [isPinching, setIsPinching] = useState(false);
    const [initialDistance, setInitialDistance] = useState(0);
    const [doubleTap, setDoubleTap] = useState(false);
    const [transitionEnabled, setTransitionEnabled] = useState(false);

    const liRef = useRef<HTMLLIElement>(null);
    const imgRef = useRef<HTMLImageElement>(null);

    const superImage = hasLoaded.current ? image : images[currentImage];

    useEffect(() => {
        hasLoaded.current = true;
    }, []);

    useEffect(() => {
        if (scale !== 1) {
            setScale(1);
            setPointX(0);
            setPointY(0);
            setPoints({ x: 0, y: 0 });
        }
    }, [currentImage, fullscreen]);

    const handleTouchStart = useCallback(
        (e: TouchEvent) => {
            if (!doubleTap) {
                setDoubleTap(true);
                setTimeout(() => {
                    setDoubleTap(false);
                }, 300);
            } else {
                if (e.touches.length === 1) {
                    e.preventDefault();
                    if (scale !== 1) {
                        setTransitionEnabled(true);
                        setZooming(false);
                        setScale(1);
                        setPointX(0);
                        setPointY(0);
                        setPoints({ x: 0, y: 0 });
                    } else {
                        const touch = e.touches[0];
                        const newScale = 2;
                        const rect = liRef.current?.getBoundingClientRect();
                        if (!rect) return;
                        const x = touch.clientX - rect.left;
                        const y = touch.clientY - rect.top;
                        const dx = (x - pointX) / scale;
                        const dy = (y - pointY) / scale;

                        setZooming(true);
                        setTransitionEnabled(true);
                        setScale(newScale);
                        setPointX(x - dx * newScale);
                        setPointY(y - dy * newScale);
                        setPoints({ x: x - dx * newScale, y: y - dy * newScale });
                    }
                    setTimeout(() => {
                        setTransitionEnabled(false);
                    }, 200);
                }
            }
            if (e.touches.length === 1 && scale !== 1) {
                // Single touch - start panning
                const touch = e.touches[0];
                setStart({ x: touch.clientX - pointX, y: touch.clientY - pointY });
                setIsPanning(true);
            } else if (e.touches.length === 2) {
                // Two fingers - start zooming
                e.preventDefault();

                const touch1 = e.touches[0];
                const touch2 = e.touches[1];

                const x = (touch1.clientX + touch2.clientX) / 2;
                const y = (touch1.clientY + touch2.clientY) / 2;

                setInitialDistance(getDistance(touch1, touch2));
                setStart({ x: x, y: y });
                setIsPinching(true);
            }
        },
        [pointX, pointY, scale, doubleTap],
    );

    const handleTouchMove = useCallback(
        (e: TouchEvent) => {
            if (isPanning && e.touches.length === 1) {
                setTransitionEnabled(false);
                const containerRect = liRef.current?.getBoundingClientRect();
                const imgRect = imgRef.current?.getBoundingClientRect();
                if (!containerRect || !imgRect) return;
                const touch = e.touches[0];

                const newPointX = touch.clientX - start.x;
                const newPointY = touch.clientY - start.y;

                if (!(newPointX > 0 || newPointX - containerRect.width < -imgRect.width)) {
                    setPointX(newPointX);
                    setPoints({ x: newPointX, y: points.y });
                }

                if (!(newPointY > 0 || newPointY - containerRect.height < -imgRect.height)) {
                    setPointY(newPointY);
                    setPoints({ x: points.x, y: newPointY });
                }
            } else if (isPinching && e.touches.length === 2) {
                e.preventDefault();
                e.stopPropagation();

                const touch1 = e.touches[0];
                const touch2 = e.touches[1];

                const rect = liRef.current?.getBoundingClientRect();
                if (!rect) return;

                const currentDistance = getDistance(touch1, touch2);
                const deltaScale = currentDistance / initialDistance;
                const newScale = scale * deltaScale;

                if (newScale <= 1) {
                    setZooming(false);
                    setScale(1);
                    setPointX(0);
                    setPointY(0);
                    setPoints({ x: 0, y: 0 });
                } else if (newScale >= 4) {
                    return;
                } else {
                    const x = (touch1.clientX + touch2.clientX) / 2 - rect.left;
                    const y = (touch1.clientY + touch2.clientY) / 2 - rect.top;
                    const newPointX = x + deltaScale * (pointX - x);
                    const newPointY = y + deltaScale * (pointY - y);
                    setZooming(true);
                    setInitialDistance(currentDistance);
                    setScale(newScale);
                    setPointY(newPointY);
                    setPointX(newPointX);
                    setPoints({ x: newPointX, y: newPointY });
                }
            }
        },
        [isPanning, isPinching, start, pointX, pointY, scale, initialDistance],
    );

    const handleTouchEnd = useCallback(() => {
        setIsPanning(false);
        setIsPinching(false);
    }, []);

    const getDistance = (touch1: Touch, touch2: Touch) => {
        const dx = touch1.clientX - touch2.clientX;
        const dy = touch1.clientY - touch2.clientY;
        return Math.hypot(dx, dy);
    };

    useEffect(() => {
        const imgElement = imgRef.current;
        if (!imgElement || !fullscreen || currentImage !== index) return;

        imgElement.addEventListener('touchmove', handleTouchMove, { passive: false });
        imgElement.addEventListener('touchstart', handleTouchStart, { passive: false });
        imgElement.addEventListener('touchend', handleTouchEnd);

        return () => {
            imgElement.removeEventListener('touchmove', handleTouchMove);
            imgElement.removeEventListener('touchstart', handleTouchStart);
            imgElement.removeEventListener('touchend', handleTouchEnd);
        };
    }, [handleTouchMove, handleTouchStart, handleTouchEnd, fullscreen, currentImage]);

    return (
        <li className={`carousel-item flex flex-col shrink-0 w-full h-full max-h-full justify-center relative`} ref={liRef}>
            <img
                ref={imgRef}
                key={`main-image-${image.uri}`}
                className={`max-w-full w-full h-full object-center object-contain`}
                title={image.description || ''}
                id={`image-${index}`}
                sizes={sizes}
                srcSet={superImage && generateSrcSet(superImage.uri)}
                data-srcset={generateSrcSet(image.uri)}
                alt={'Galleribilde'}
                style={{
                    transformOrigin: '0px 0px',
                    transition: transitionEnabled ? 'transform 0.3s ease-in-out' : 'none',
                    transform: scale === 1 ? 'none' : `translate(${pointX}px, ${pointY}px) scale(${scale})`,
                }}
            />
            <div
                className={`absolute bottom-0 text-caption w-full text-m s-text-inverted image-description-mobile p-16 ${
                    image.description === undefined ? 'hidden' : ''
                }`}>
                <span>{image.description}</span>
            </div>
        </li>
    );
};

const checkSnapPos = (currentImage: number, listElement: HTMLUListElement, setScrollPosition: (currentImage: number) => void) => {
    const outOfSync = listElement.scrollLeft % listElement.clientWidth !== 0;
    if (outOfSync) {
        setScrollPosition(currentImage);
    }
};

const [snapChecker] = debounce(checkSnapPos, 1200);
